import React, { memo, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FitzaaLoader } from './FitzaaLoader';
import { useErrorInterceptor } from '../hooks/useErrorInterceptor';
import { getSubscription } from '../services/SubscriptionService';
import { hasActiveSubscription } from '../utils/planLimits';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requireAuth?: boolean;
    redirectTo?: string;
    requireSubscription?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = memo(({
    children,
    requireAuth = true,
    redirectTo = '/login',
    requireSubscription = false
}) => {
    const { isLoading, isAuthenticated, user } = useAuth();
    const location = useLocation();
    const { handleError } = useErrorInterceptor();
    const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
    const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);

    // Monitor authentication state for errors
    useEffect(() => {
        if (!isLoading && requireAuth && !isAuthenticated) {
            // If we're not loading and not authenticated, check if there was an error
            const error = sessionStorage.getItem('auth.error');
            if (error) {
                try {
                    const errorData = JSON.parse(error);
                    handleError(errorData);
                    sessionStorage.removeItem('auth.error');
                } catch {
                    // If we can't parse the error, just clear it
                    sessionStorage.removeItem('auth.error');
                }
            }
        }
    }, [isLoading, isAuthenticated, requireAuth, handleError]);

    // Check subscription for app routes or app login if required
    useEffect(() => {
        const checkSubscription = async () => {
            if (!requireSubscription || !isAuthenticated || !user || isLoading) {
                return;
            }

            // Determine if this is an app route that requires subscription
            const isAppRoute = location.pathname.startsWith('/home') || 
                               location.pathname.startsWith('/dashboard') ||
                               location.pathname.startsWith('/challenge') ||
                               location.pathname.startsWith('/leaderboard') ||
                               location.pathname.startsWith('/settings') ||
                               location.pathname.startsWith('/join-challenges') ||
                               location.pathname.startsWith('/user-settings');
            
            // Also check subscription when on app login page if authenticated
            const isAppLogin = location.pathname === '/app/login';

            if (!isAppRoute && !isAppLogin) {
                return;
            }

            setIsCheckingSubscription(true);
            
            try {
                // Get organizationId - check user's organizationId first, fall back to user.id for individual subscriptions
                let orgId: string | null = null;
                
                if (user.organizationId && 
                    user.organizationId !== 'default-org' && 
                    user.organizationId !== 'default-org-id' && 
                    user.organizationId !== 'pending' &&
                    user.organizationId !== 'individual') {
                    orgId = user.organizationId;
                } else {
                    // For individual subscriptions, use user.id
                    orgId = user.id;
                }

                if (!orgId) {
                    setHasSubscription(false);
                    setIsCheckingSubscription(false);
                    return;
                }

                // Check subscription status
                const subscription = await getSubscription(orgId);
                const active = hasActiveSubscription(subscription);
                setHasSubscription(active);

                // Also check if user is part of an organization with active subscription
                if (!active && user.organizationId && user.organizationId !== orgId) {
                    const orgSubscription = await getSubscription(user.organizationId);
                    if (hasActiveSubscription(orgSubscription)) {
                        setHasSubscription(true);
                    }
                }
            } catch (error) {
                console.error('[ProtectedRoute] Error checking subscription:', error);
                setHasSubscription(false);
            } finally {
                setIsCheckingSubscription(false);
            }
        };

        checkSubscription();
    }, [isAuthenticated, user, isLoading, requireSubscription, location.pathname]);

    // Show loading spinner while checking authentication or subscription
    if (isLoading || (requireSubscription && isAuthenticated && (isCheckingSubscription || hasSubscription === null))) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <FitzaaLoader size="large" message={requireSubscription && isAuthenticated ? "Checking subscription..." : "Loading..."} />
            </div>
        );
    }

    // If subscription is required and user doesn't have active subscription, redirect to onboarding
    if (requireSubscription && isAuthenticated && hasSubscription === false) {
        return <Navigate to="/onboarding" replace />;
    }

    // If authentication is required and user is not authenticated
    if (requireAuth && !isAuthenticated) {
        // For subscription/admin routes, use admin login page with return URL
        const isSubscriptionRoute = location.pathname.startsWith('/subscription') || 
                                     location.pathname.startsWith('/onboarding') ||
                                     (location.pathname.startsWith('/admin') && location.pathname !== '/admin/login');
        if (isSubscriptionRoute) {
            const returnUrl = location.pathname + (location.search || '');
            return <Navigate to={`/admin/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
        }
        // For app routes (home, dashboard, challenges, etc.), use app login
        const isAppRoute = location.pathname.startsWith('/home') || 
                           location.pathname.startsWith('/dashboard') ||
                           location.pathname.startsWith('/challenge') ||
                           location.pathname.startsWith('/leaderboard') ||
                           location.pathname.startsWith('/settings') ||
                           location.pathname.startsWith('/join-challenges') ||
                           location.pathname.startsWith('/user-settings');
        if (isAppRoute) {
            return <Navigate to="/app/login" state={{ from: location }} replace />;
        }
        // Default: redirect to admin login (for website/web routes)
        return <Navigate to={redirectTo || '/admin/login'} state={{ from: location }} replace />;
    }

    // If authentication is not required and user is authenticated
    if (!requireAuth && isAuthenticated) {
        // For admin login page, if already authenticated, redirect to subscription management
        if (location.pathname === '/admin/login' || location.pathname === '/subscription/login' || location.pathname === '/login') {
            const returnUrl = new URLSearchParams(location.search).get('returnUrl') || '/subscription';
            return <Navigate to={returnUrl} replace />;
        }
        // For app login page, if already authenticated, check subscription before redirecting
        if (location.pathname === '/app/login') {
            // If subscription check hasn't completed yet, wait
            if (requireSubscription && (isCheckingSubscription || hasSubscription === null)) {
                return (
                    <div className="flex items-center justify-center min-h-screen">
                        <FitzaaLoader size="large" message="Checking subscription..." />
                    </div>
                );
            }
            // If subscription is required and not active, redirect to onboarding
            if (requireSubscription && hasSubscription === false) {
                return <Navigate to="/onboarding" replace />;
            }
            return <Navigate to="/home" replace />;
        }
        // Default: redirect to dashboard
        return <Navigate to="/dashboard" replace />;
    }

    // User is authenticated and can access the protected route
    return <>{children}</>;
});

ProtectedRoute.displayName = 'ProtectedRoute';

export default ProtectedRoute; 